import { useCallback, useMemo } from 'react'
import { getBoxFileId } from '../helpers/boxFileLink'
import {
  BOX_VIEWER_MODE,
  BoxViewerMode,
  getEmbedLink,
  hideBoxPreview,
  setBoxViewerLink,
} from '../redux/box/actions'
import { isBoxViewerOpen as isBoxViewerOpenSelector } from '../redux/box/selectors'
import { useDispatch, useSelector } from '../redux/hooks'

/**
 * Marks a row that drives `syncPreviewToRow` on click.
 *
 * BoxViewer's click-outside handler runs in the capture phase, i.e. before the
 * row's React `onClick`, and its close dispatch flushes a synchronous re-render
 * — so by the time the row handler runs, `isPreviewOpen` is already false and
 * `syncPreviewToRow` no-ops. The panel closes instead of swapping documents.
 *
 * Spreading this onto the row tells the viewer to stand down for that click:
 * the row owns the decision, and `syncPreviewToRow` swaps to its document or
 * closes when it has none. Rows without it keep closing the panel as any other
 * outside click does.
 */
export const PREVIEW_SYNC_ROW_ATTR = 'data-preview-sync'
export const previewSyncRowProps = { [PREVIEW_SYNC_ROW_ATTR]: true }

/**
 * A link inside a dialog or a temporary drawer opens the centered viewer, which stacks
 * above MUI's modal layer. The side drawer shares that layer and would open underneath.
 */
export const boxViewerModeFor = (element: Element | null): BoxViewerMode =>
  element?.closest('.MuiModal-root:not(.MuiPopover-root)')
    ? BOX_VIEWER_MODE.MODAL
    : BOX_VIEWER_MODE.DRAWER

type OpenBoxPreview = (boxLink?: string | null, mode?: BoxViewerMode) => Promise<void>

/**
 * Opens the global BoxViewer on a Box link. Kept apart from `useBoxPreview` so a list of
 * file links doesn't subscribe every row to the viewer's open state.
 */
export const useOpenBoxPreview = (): OpenBoxPreview => {
  const dispatch = useDispatch()

  // A row with no attached document must never open an empty panel, so an absent
  // link is a no-op rather than a viewer seeded with nothing to render.
  return useCallback(
    async (boxLink?: string | null, mode: BoxViewerMode = BOX_VIEWER_MODE.DRAWER) => {
      if (!boxLink) {
        return
      }
      const boxFileId = getBoxFileId(boxLink)
      dispatch(setBoxViewerLink('loading', mode, boxFileId ?? undefined))
      await dispatch(getEmbedLink(boxFileId ?? (boxLink.split('/').pop() || null)))
    },
    [dispatch],
  )
}

/**
 * Previews a Box file link in place. Anything else, such as a folder, still opens in a new
 * tab. `anchor` is the clicked element, which decides between the drawer and the modal.
 */
export const useOpenBoxLink = (): ((link?: string | null, anchor?: Element | null) => void) => {
  const openPreview = useOpenBoxPreview()

  return useCallback(
    (link?: string | null, anchor: Element | null = null) => {
      if (!link) {
        return
      }
      if (!getBoxFileId(link)) {
        window.open(link, '_blank', 'noopener,noreferrer')
        return
      }
      void openPreview(link, boxViewerModeFor(anchor))
    },
    [openPreview],
  )
}

interface IUseBoxPreview {
  isPreviewOpen: boolean
  openPreview: OpenBoxPreview
  closePreview: () => void
  syncPreviewToRow: (boxLink?: string | null) => Promise<void>
}

/**
 * Single entry point for driving the global BoxViewer drawer (mounted once in
 * MainLayout) from a table row.
 *
 * The open sequence — seed the viewer in its loading state, pull the Box file id
 * off the tail of the share link, then resolve the embed URL — was copy-pasted
 * across Account Activity, its client-portal twin, the Fundings/Wires table and
 * the funding request screens. Four copies meant four places to fix when the
 * empty-panel guard or the close behaviour changed, which is how they drifted.
 *
 * Link *resolution* deliberately stays with the caller: which field holds the
 * document differs per surface (`borrowingBase.clientBoxLink` vs `boxLink`, gated
 * on user role and activity type). This hook owns only the mechanism.
 */
export const useBoxPreview = (): IUseBoxPreview => {
  const dispatch = useDispatch()
  const isPreviewOpen = useSelector(isBoxViewerOpenSelector)
  const openPreview = useOpenBoxPreview()

  const closePreview = useCallback(() => {
    dispatch(hideBoxPreview())
  }, [dispatch])

  /**
   * Row-click handler for a panel that is *already* open: swap it to this row's
   * document, or close it when the row has none. Does nothing while the panel is
   * closed — selecting a row is not a request to preview it, so plain row
   * selection keeps behaving as it always has.
   */
  const syncPreviewToRow = useCallback(
    async (boxLink?: string | null) => {
      if (!isPreviewOpen) {
        return
      }
      if (!boxLink) {
        closePreview()
        return
      }
      await openPreview(boxLink)
    },
    [isPreviewOpen, openPreview, closePreview],
  )

  return useMemo(
    () => ({ isPreviewOpen, openPreview, closePreview, syncPreviewToRow }),
    [isPreviewOpen, openPreview, closePreview, syncPreviewToRow],
  )
}
